import { redirect } from '@tanstack/react-router';
import { useAuthStore } from '@/stores/auth.store';

/**
 * Route guard for the office scheduling cockpit (the dispatch board and the
 * live dispatch view). Those surfaces expose the whole technician roster and
 * flota-wide controls, so a field technician — identified by an OWN
 * `calendar:view` scope — has no business there and is bounced to their own
 * schedule instead.
 *
 * Office users (TEAM/ALL scope, or no scope entry at all) fall through
 * untouched. An OWN-scoped account with no technician link is an unusual
 * restricted office role rather than a field tech, so it goes to the dashboard
 * home rather than My Day — which would itself bounce a link-less user and
 * risk a redirect loop.
 */
export function assertOfficeScheduleAccess(): void {
  const state = useAuthStore.getState();
  // Optional-read: a partially-hydrated auth payload may lack permissionScopes;
  // a missing entry means "not an OWN-scoped tech" → fall through untouched.
  if (state.permissionScopes?.['calendar:view'] !== 'OWN') return;
  // `replace: true` — this is enforcement, not navigation, so it must not leave
  // the blocked dispatch URL on the history stack (repeated entries would let a
  // back-button bounce between here and the target).
  throw redirect({
    to: state.technicianId != null ? '/dashboard/my-day' : '/dashboard/home',
    replace: true,
  });
}
